import { useState } from 'react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { Ticket } from 'lucide-react';
import type { AmazonCoupon, AmazonCouponPoolStatus, AmazonMarketplace } from '@staff-app/shared-types';
import {
  formatDiscount,
  LOW_POOL_THRESHOLD,
  MARKETPLACES,
  useAllocateAmazonCoupon,
} from '../hooks/useAmazonCoupons';

/**
 * The allocate card: marketplace · customer · reason → one code out of the pool.
 *
 * NO quote step, unlike DemoCouponForm. That card shows what a coupon is worth before minting
 * because it hands out a dollar amount; this one hands out a percentage, which needs no catalog
 * price and has nothing to get wrong.
 *
 * What it has instead is a POOL CHECK. A mint cannot run out; a pool can, and discovering that
 * after telling a customer they have a code is the failure this card is shaped to prevent — so
 * the marketplace picker states the remaining count per market and the button refuses an empty one
 * before the mutation is ever sent.
 */
export function AmazonCouponForm({
  pools,
  poolsLoading,
  onAllocated,
}: {
  pools: AmazonCouponPoolStatus[];
  poolsLoading?: boolean;
  onAllocated: (c: AmazonCoupon) => void;
}) {
  // Narrowed, not plain string: the mutation input takes the union, and the Select only ever
  // emits a value from MARKETPLACES — so the cast lives at that one boundary rather than being
  // re-asserted at the call site where a typo would reach the server.
  const [marketplace, setMarketplace] = useState<AmazonMarketplace | ''>('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [reason, setReason] = useState('');

  const [allocate, { loading: allocating, error: allocateError }] = useAllocateAmazonCoupon();

  const poolFor = (m: string) => pools.find((p) => p.marketplace === m);
  const selected = marketplace ? poolFor(marketplace) : undefined;
  const available = selected?.available ?? 0;

  // Mirrors the server's rule (amazon-coupons.service.ts) — permissive on purpose: this records
  // who the code went to, it is not a login, so a stricter pattern rejects real addresses to
  // protect nothing. Same call DemoCouponForm made.
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim());
  const canSubmit =
    !!marketplace && available > 0 && emailLooksValid && reason.trim().length >= 3 && !allocating;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const res = await allocate({
        variables: {
          input: { marketplace, customerEmail: customerEmail.trim(), reason: reason.trim() },
        },
      });
      const created = res.data?.allocateAmazonCoupon;
      if (!created) return;
      onAllocated(created);
      // The MARKET stays, the customer does not — issuing several codes for one market in a row is
      // the normal case, while a leftover address is how one customer's name lands on another's
      // coupon. Same split DemoCouponForm settled on.
      setCustomerEmail('');
      setReason('');
    } catch {
      // Surfaced through `allocateError` below. Swallowed here because an unhandled rejection is
      // not an error message — it is a blank card and a console entry.
    }
  };

  return (
    <div className="rounded-lg border p-4" data-testid="amazon-coupon-form">
      <h3 className="flex items-center gap-2 font-semibold">
        <Ticket className="h-4 w-4" />
        {'Allocate a code to hand to a customer'}
      </h3>
      {/* States the delivery boundary FIRST. "Give a customer a coupon" read as though the app
          emailed it; nothing here sends anything, and a staffer who assumed otherwise would close
          a ticket having sent the customer nothing at all. */}
      <p className="mt-1 text-sm text-muted-foreground">
        {'Takes one pre-generated single-use claim code out of the pool and records who it is for. '}
        <strong className="text-foreground">{'It does not email anyone'}</strong>
        {' — you copy the code and send it yourself. The customer enters it at Amazon checkout '}
        {'under “Gift cards & promotional codes”, where the discount appears at the payment step, '}
        {'not on the product page.'}
      </p>

      {/* items-start is load-bearing, not cosmetic. Grid columns stretch to the tallest by
          default, and these two columns hold different numbers of children — the email field
          always carries a helper paragraph, the marketplace one only carries its pool lines once
          something is chosen. Stretched, each column distributes its rows over a different height
          and the Select and the Input stop lining up even though both are h-9. Anchoring the
          columns to the top makes every row content-sized, so the labels and controls sit on the
          same baselines whatever helper text is or is not showing. */}
      <div className="mt-4 grid items-start gap-4 md:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="amazon-marketplace">{'Marketplace'}</Label>
          <Select value={marketplace} onValueChange={(v) => setMarketplace(v as AmazonMarketplace)}>
            <SelectTrigger id="amazon-marketplace" data-testid="amazon-marketplace">
              <SelectValue placeholder={poolsLoading ? 'Loading…' : 'Choose a marketplace'} />
            </SelectTrigger>
            <SelectContent>
              {MARKETPLACES.map((m) => {
                const pool = poolFor(m.value);
                const count = pool?.available ?? 0;
                return (
                  // Disabled rather than hidden: a market with no codes is a fact the operator
                  // needs ("we're out of UK"), and hiding it reads as "we don't sell there".
                  <SelectItem key={m.value} value={m.value} disabled={count === 0}>
                    {`${m.label} — ${count === 0 ? 'no codes' : `${count} left`}`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {selected ? (
            <p className="text-xs text-muted-foreground" data-testid="amazon-pool-detail">
              {`${formatDiscount(selected.discountAmount, selected.currency)} on ${selected.sku} (${selected.asin})`}
            </p>
          ) : null}
          {selected && available > 0 && available < LOW_POOL_THRESHOLD ? (
            <p className="text-xs text-amber-600" data-testid="amazon-pool-low">
              {`Only ${available} left — import another batch from Seller Central soon.`}
            </p>
          ) : null}
        </div>

        <div className="grid gap-1.5">
          <Label htmlFor="amazon-customer">{'Customer email'}</Label>
          <Input
            id="amazon-customer"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="buyer@example.com"
            data-testid="amazon-customer-email"
          />
          <p className="text-xs text-muted-foreground">
            {'A record of who it is for — not a delivery address, and not a restriction. Nothing '}
            {'is sent here, and anyone holding the code can redeem it.'}
          </p>
        </div>

        <div className="grid gap-1.5 md:col-span-2">
          <Label htmlFor="amazon-reason">{'Reason'}</Label>
          <Input
            id="amazon-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this customer is getting money off"
            data-testid="amazon-reason"
          />
          <p className="text-xs text-muted-foreground">
            {'Required. There is no approver on this path, so this is the only record of why the '}
            {'code exists.'}
          </p>
        </div>
      </div>

      {allocateError ? (
        <div
          className="mt-4 rounded-md border border-destructive p-3 text-sm text-destructive"
          data-testid="amazon-allocate-error"
        >
          {allocateError.message}
        </div>
      ) : null}

      <div className="mt-4 flex items-center gap-3">
        <Button
          onClick={() => {
            void submit();
          }}
          disabled={!canSubmit}
          data-testid="amazon-allocate-submit"
        >
          {allocating ? 'Allocating…' : 'Allocate a code'}
        </Button>
        {marketplace && available === 0 ? (
          <span className="text-sm text-destructive" data-testid="amazon-pool-empty">
            {'No codes left for that marketplace — a batch must be imported first.'}
          </span>
        ) : null}
      </div>
    </div>
  );
}
